import { GetServerSideProps } from 'next';
import CommonExperienceDetailPage from '@/components/experience-detail/CommonExperienceDetailPage';
import PackageBookingPreviewClientPage from '@/components/package-booking-preview/PackageBookingPreviewClientPage';
import { getExperienceDetailsByBookingId } from '@/services/bookingsService';
import { useHardBackNavigation } from '@/utils/safeRouterPush';

export const getServerSideProps: GetServerSideProps = async context => {
  const { slug } = context.params as { slug: string };
  const bookingId = context.query?.booking as string | undefined;
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || '';
  const detailsUrl = bookingId
    ? `${baseUrl}/travel/packages/v1/details/${slug}?booking=${encodeURIComponent(bookingId)}`
    : `${baseUrl}/travel/packages/v1/details/${slug}`;

  try {
    // When we have a booking ID, fetch latest experience from booking endpoint (uses snapshot when modified)
    let packagePayload: unknown = null;
    let booking: unknown = undefined;

    if (bookingId) {
      try {
        const experienceRes = await getExperienceDetailsByBookingId(bookingId);
        if (
          experienceRes.success &&
          experienceRes.data?.bookingType === 'package' &&
          experienceRes.data?.experienceDetails != null
        ) {
          packagePayload = experienceRes.data.experienceDetails;
        }
      } catch (e) {
        console.warn(
          'Booking experience-details failed, falling back to details by slug:',
          e
        );
      }
    }

    // Always fetch travel details: needed for booking object and as fallback when no bookingId or experience-details failed
    const response = await fetch(detailsUrl);
    const data = await response.json();
    console.log('[package-details][SSR] fetched details', {
      detailsUrl,
      bookingId,
      ok: response.ok,
    });
    const detailsPayload = data.data || null;
    const bookingFromDetails =
      data.booking ??
      (detailsPayload &&
      typeof detailsPayload === 'object' &&
      'booking' in detailsPayload
        ? (detailsPayload as { booking?: unknown }).booking
        : undefined);

    if (bookingFromDetails !== undefined) booking = bookingFromDetails;

    const payload = packagePayload != null ? packagePayload : detailsPayload;

    const initialData =
      payload != null && typeof payload === 'object' && booking !== undefined
        ? { ...payload, booking }
        : payload;

    return {
      props: {
        initialData,
        slug,
        type: 'package' as const,
        ...(bookingId && { bookingId }),
        ...(booking !== undefined && { booking }),
        isPackageBookingPreview: Boolean(bookingId && booking !== undefined),
      },
    };
  } catch (error) {
    console.error('Error fetching package:', error);
    return {
      props: {
        initialData: null,
        slug,
        type: 'package' as const,
        ...(bookingId && { bookingId }),
        isPackageBookingPreview: false,
      },
    };
  }
};

interface PackageDetailsProps {
  initialData: unknown;
  slug: string;
  type: 'package';
  bookingId?: string;
  booking?: unknown;
  isPackageBookingPreview?: boolean;
}

export default function PackageDetails({
  initialData,
  slug,
  type,
  bookingId,
  booking,
  isPackageBookingPreview,
}: PackageDetailsProps) {
  // Heavy journey pages can hang Next.js client transitions, so browser back
  // updates the URL while this page stays mounted. Full-load the history target.
  useHardBackNavigation();

  if (isPackageBookingPreview && bookingId && booking) {
    return (
      <PackageBookingPreviewClientPage
        bookingId={bookingId}
        booking={booking}
        packageData={initialData}
        packageSlug={slug}
      />
    );
  }

  return (
    <CommonExperienceDetailPage
      initialData={initialData}
      slug={slug}
      type={type}
      bookingId={bookingId}
      booking={booking}
    />
  );
}
